import { getMaxLevel, getSvgHeight, splitWordsByMaxNumLines, trackRawValues, textSplitForTooltip } from './utilities'
import { setOptions } from '../utilities/sankey_utilites'
import { seedFakeData } from './seed_fake_data'

/**
 * @param {String} selector  - div to hold the sankey
 * @param {Object} sankeyData - JSON data for rendering the sankey
 * @param {Object} sankeyOptions - custom options for styling the sankey
 */
function CompanySankey (selector, sankeyData, sankeyOptions) {
  // d3 has not been installed through NPM, so eslint will warn that d3 not defined
  const container = d3.select(selector) // eslint-disable-line no-undef
  const data = sankeyData
  const maxLevel = getMaxLevel(data.nodes)
  seedFakeData(data, maxLevel)

  // Raw calculated values that's passed in
  // NOTE: when sankey(data) is called, the node values will be recalculated
  // However, it doesn't take into account nodes connected to the same node
  // which messes up the final value totals
  const rawValues = trackRawValues(data.nodes)

  // heightMultiplier - For setting vertical spacing between nodes
  // and determining the overall height of the diagram.
  // It needs to be set before options/defaultOptions since height will be passed into those objects
  const heightMultiplier = 85
  const height = getSvgHeight(data.nodes, heightMultiplier)
  let maxYCoord = height

  let options = (sankeyOptions || {})
  const defaultOptions = {
    width: 1054, // width of SVG, based on width of other divs when viewing at app-min-width
    height: height, // height of SVG
    boldFontWeight: 600,
    labelFontSize: 12,
    svgPaddingY: 100, // add extra space to the nodes near the ends, and room for label text
    svgPaddingX: 50,
    headerTextMaxWidth: 950, // for positioning header text (based on auto-generated values node.x0)
    sankey: {
      nameMaxNumLines: 2, // number of lines to split node name text by
      nameLineYOffset: [-10, 3], // offset for each line in the name text
      maxNameStringLength: 15, // max length for each line in node name text
      nodeWidth: 54,
      nodeHeight: 34,
      nodePadding: 100,
      nodeRx: 18, // roundness of node edges
      nodeOutlineColor: '#8AC7ED',
      nodeHoverOutlineColor: '#0033A0',
      nodeOutlineThickness: 1,
      nodeFillColor: '#FFFFFF',
      nodeTextColor: '#30303A',
      nodePercentageYOffest: 22,
      nodeNameFontSize: 11,
      nodeNameYOffset: -8,
      nodePercentageFontSize: 13,
      linkOpacity: 0.25,
      linkOpacityActive: 0.65,
      linkBaseThickness: 34, // base expected line thickness at 100%
      linkMinThickness: 2, // expected line thickness for dashed lines
      linkDashArray: 5 // controls how big the gaps are in a dashed line
    },
    linkColors: {
      default: '#0033A0'
    },
    showTooltip: true,
    tooltip: {
      theme: 'black_on_white_small', // theme for tooltip
      maxWidth: 160, // tooltip maxWidth,
      position: 'top'
    }
  }
  options = setOptions(defaultOptions, options)

  let rootNode
  let nodeIndex = 0 // For determining what order to append elements on the DOM (in case someome tabs through the nodes)
  let positionIndex = 0 // For figuring out node Y positioning
  let svg
  let sankey
  let graph
  let nodes
  let nodeContent // container for wrapping the pill and the percentage text
  let links
  let extensionContainer

  /** intializes some of the sankey elements and starts drawing */
  (function initialize () {
    svg = container.append('svg')
      .attr('width', options.width) // roughly the width of a section at app_min_width (excluding padding and margin)
      .attr('height', options.height + options.svgPaddingY)
      .attr('class', 'flex center')
      .attr('id', 'company_sankey')

    sankey = d3.sankey() // eslint-disable-line no-undef
      .nodeWidth(options.sankey.nodeWidth)
      .nodePadding(options.sankey.nodePadding)
      .size([options.width - options.svgPaddingX, options.height - options.svgPaddingY])
      .nodeId((node) => node.id) // NOTE: use this to use the id specified in the data

    // initialize sankey data - makes a pseudo tree structure
    graph = sankey(data)

    // recursively index the nodes and update the positioning
    rootNode = data.nodes.filter((d) => { return d.level === 0 })[0]
    indexNodes(rootNode, positionIndex)
    getYCoordinate()
    adjustLinkEndPoints(rootNode)

    drawSankey()
  })()

  /**
   * Recursively iterate through the nodes
   * @param {*} node - node to index and iterate
   */
  function indexNodes (node) {
    // update index if the node has NOT been previously indexed
    // NOTE: product lines can have multiple RBR parents
    node.value = rawValues[node.id]
    if (node.positionIndex === undefined) {
      node.positionIndex = positionIndex

      node.index = nodeIndex
      nodeIndex++
    }

    if (node.sourceLinks.length > 0) {
      // sort the childLinks by the raw values
      const childLinks = node.sourceLinks.sort(function (a, b) {
        return rawValues[b.target.id] - rawValues[a.target.id]
      })

      // index the child nodes
      childLinks.forEach(function (link, index) {
        if (index > 0 && link.target.positionIndex === undefined) {
          positionIndex++
        }
        indexNodes(link.target)
      })
    }
  }

  /** draw the elements on on sankey */
  function drawSankey () {
    drawLinks()
    drawLinkExtensionContainer(rootNode)
    drawNodes()
    addNodeNameLabel()
    addNodePercentageLabel()
    addLevelLabels()

    // Update SVG height after rendering all the nodes and links
    updateSvgHeight(height, maxYCoord, heightMultiplier, options.svgPaddingY)
    addHoverEffects()

    if (options.showTooltip) {
      addTooltips(nodeContent)
    }
  }

  /** draw the connectors between nodes */
  function drawLinks () {
    // render links
    links = svg.append('g')
      .attr('class', 'links-container')
      .attr('transform', `translate(${options.sankey.nodeWidth / 2}, ${options.svgPaddingY + (Math.abs(options.sankey.nodeNameYOffset) * 2)})`)
      .selectAll('.sankey-link')
      .data(graph.links.sort((a, b) => { return a.target.index - b.target.index })) // append links on DOM in order
      .enter().append('path')
      .attr('class', 'sankey-link')
      .attr('d', d3.sankeyLinkHorizontal()) // eslint-disable-line no-undef

    // add styling to links
    links.style('stroke', options.linkColors.default)
      .style('stroke-opacity', options.sankey.linkOpacity)
      .style('fill', 'none')
      .style('stroke-dasharray', function (d) {
        if (rawValues[d.target.id] === 0) {
          // display dashed line if the value is 0
          return options.sankey.linkDashArray
        } else { // display solid lines
          return 0
        }
      })
      .attr('stroke-width', function (d) {
        if (d.target.value === 0) {
          return options.sankey.linkMinThickness
        } else {
          // line thickness is dependent on value node value
          return calculateLineThickness(d.target.value)
        }
      })
      .attr('data-source-id', function (d) { return d.source.id })
      .attr('data-target-id', function (d) { return d.target.id })
  }

  /**
   * Draw container for holding the link extensions, and draw the link extensions
   * @param {*} node
   */
  function drawLinkExtensionContainer (node) {
    extensionContainer = svg.append('g')
      .attr('class', 'extension-container')
      .attr('transform', `translate(${options.sankey.nodeWidth / 2}, ${options.svgPaddingY + (Math.abs(options.sankey.nodeNameYOffset) * 2)})`)

    drawLinkExtensions(node)
  }

  /** Draw background lines behind the nodes to prevent weird gaps due to rounded corners
   * @param {Object} node
  */
  function drawLinkExtensions (node) {
    // Draw extension on right side of node
    if (node.sourceLinks.length > 0) {
      const links = node.sourceLinks.sort((a, b) => { return b.target.value - a.target.value })
      let cumulativeOffset = 0
      links.forEach(function (link) {
        const lineThickness = calculateLineThickness(link.target.value)
        const x1 = link.source.x0 + (options.sankey.nodeWidth / 2)
        const x2 = link.source.x0 + options.sankey.nodeWidth
        let y1 = link.y0

        // if the thickness exceeds the base thickness, angle the line so it appears to come out of the other node
        // only angle the link if the links are stacked
        if (Math.round(cumulativeOffset + lineThickness) > options.sankey.linkBaseThickness && node.stackedLinks) {
          y1 -= options.sankey.nodeHeight / 4
        }

        if (hasNoExtension(x1, x2, y1, link.y0)) {
          drawLinkExtension(x1, x2, y1, link.y0, lineThickness, link.source.id, link.target.id, node.value)
        }

        cumulativeOffset += lineThickness
        drawLinkExtensions(link.target)
      })
    }

    // Draw extension on left side of node
    if (node.targetLinks.length > 0) {
      node.targetLinks.forEach(function (link) {
        const lineThickness = calculateLineThickness(link.target.value)
        const x1 = link.target.x0
        const x2 = link.target.x0 + (options.sankey.nodeWidth / 2)

        // Draw extension if:
        // 1. There are no previously drawn extensions with a similar position
        // 2. The line is not too thin (otherwise it'll be covered by the node)

        const wasNotPreviouslyRendered = hasNoExtension(x1, x2, link.y1, link.y1)
        const lineThickEnough = lineThickness > options.sankey.linkBaseThickness / 4
        const isFakeNode = node.type === 'fake_node'

        if (wasNotPreviouslyRendered && (lineThickEnough || isFakeNode)) {
          drawLinkExtension(x1, x2, link.y1, link.y1, lineThickness, link.source.id, link.target.id, node.value)
        }
      })
    }
  }

  /**
   * Check if there's a previously drawn line with the same dimensions
   * @param {Number} x1
   * @param {Number} x2
   * @param {Number} y1
   * @param {Number} y2
   * @returns {Boolean} true if there were NO previously rendered lines with the exact same coordinates
   */
  function hasNoExtension (x1, x2, y1, y2) {
    return document.querySelector(`line.sankey-link[x1="${x1}"][x2="${x2}"][y1="${y1}"][y2="${y2}"]`) === null
  }

  /**
   * Draw fake link extension behind the node
   * @param {Number} x1
   * @param {Number} x2
   * @param {Number} y1
   * @param {Number} y2
   * @param {Number} lineThickness
   * @param {String} sourceId
   * @param {String} targetId
   * @param {Number} value value of node
   */
  function drawLinkExtension (x1, x2, y1, y2, lineThickness, sourceId, targetId, value) {
    const extension = extensionContainer.append('line')
      .attr('class', 'sankey-link')
      .attr('x1', x1)
      .attr('x2', x2)
      .attr('y1', y1)
      .attr('y2', y2)
      .attr('stroke', options.linkColors.default)
      .attr('stroke-width', lineThickness)
      .style('stroke-opacity', options.sankey.linkOpacity)
      .attr('data-source-id', sourceId)
      .attr('data-target-id', targetId)

    if (value === 0) { // make the fake line dashed
      extension.style('stroke-dasharray', options.sankey.linkDashArray)
    }
  }

  function drawNodes () {
    // add graphic element that holds the nodes
    nodes = svg.append('g')
      .attr('class', 'nodes-container')
      .attr('transform', `translate(${options.sankey.nodeWidth / 2}, ${options.svgPaddingY - options.sankey.nodeOutlineThickness})`)
      .selectAll('.node')
      .data(graph.nodes.sort((a, b) => { return a.index - b.index }).filter((d) => { return d.type !== 'fake_node' })) // append nodes on DOM in order
      .enter().append('g')
      .attr('class', 'node')
      .attr('data-node-id', function (d) { return d.id })
      .attr('data-node-level', function (d) { return d.level })

    // container for wrapping the pill and the percentage text
    nodeContent = nodes.append('g').attr('class', 'node-content cursor-default')

    // add actual node element (rounded-pill)
    nodeContent.append('rect')
      .attr('x', function (d) { return d.x0 })
      .attr('y', function (d) { return d.y })
      .attr('rx', options.sankey.nodeRx)
      .attr('height', options.sankey.nodeHeight)
      .attr('width', options.sankey.nodeWidth)
      .style('fill', options.sankey.nodeFillColor)
      .style('stroke', options.sankey.nodeOutlineColor)
  }

  /** add node name on top of the node */
  function addNodeNameLabel () {
    // nodes => Selection object
    // nodes._groups[0] => array of actual elements
    nodes._groups[0].forEach(function (node) {
      const currNodeData = node.__data__

      // 1. Split the Node name into multiple lines (max: 2 for now)
      const linesOfNameText = splitWordsByMaxNumLines(currNodeData.name, options.sankey.maxNameStringLength, options.sankey.nameMaxNumLines)

      // 2. Calculate the y coordinates for each line of text
      const baseYCoordinate = currNodeData.y
      let yCoordinates = [baseYCoordinate]
      if (linesOfNameText.length > 1) {
        // if we have more than 1 line of text, add some offsets to the Y position of each line
        yCoordinates = options.sankey.nameLineYOffset.map((d) => { return d + baseYCoordinate })
      }

      // 3. Render each line of text that corresponds to the node name
      linesOfNameText.forEach(function (line, index) {
        d3.select(node).append('text') // eslint-disable-line no-undef
          .attr('class', 'name-text')
          .attr('x', function (d) { return d.x0 })
          .attr('y', yCoordinates[index])
          .text(line)
          .style('fill', options.sankey.nodeTextColor)
          .style('font-size', options.sankey.nodeNameFontSize + 'px')

          // NOTE: this was the closest approximation I got for centering the text:
          // 1. anchor the text to the middle of the node
          // 2. offset X by half the width of the node
          .style('text-anchor', 'middle')
          .style('transform', `translate(${options.sankey.nodeWidth / 2}px, ${options.sankey.nodeNameYOffset}px)`)
      })
    })
  }

  /** add node percentage text inside the node */
  function addNodePercentageLabel () {
    nodeContent.append('text')
      .attr('class', 'percentage-text')
      .attr('x', function (d) { return d.x0 })
      .attr('y', function (d) { return d.y + options.sankey.nodePercentageYOffest })
      .text(function (d) { return nodePercentageValues(d.id) })
      .style('fill', options.sankey.nodeTextColor)
      .style('font-size', options.sankey.nodePercentageFontSize + 'px')
      .style('font-weight', options.boldFontWeight)

      // NOTE: this was the closest approximation I got for centering the text:
      // 1. anchor the text to the middle of the node
      // 2. offset X by half the width of the node
      .style('text-anchor', 'middle')
      .style('transform', `translate(${options.sankey.nodeWidth / 2}px, 0px)`)
  }

  /**
   * @param {String} nodeId
   * @returns {String} formatted percentage text to display in the node
   */
  function nodePercentageValues (nodeId) {
    const rawValue = rawValues[nodeId]
    const displayText = (rawValue > 0 && rawValue < 0.1) ? '< 0.1' : rawValue.toFixed(1)

    return `${displayText}%`
  }

  /** append  lens/level label on top of the chart */
  function addLevelLabels () {
    const labels = svg.append('g')
      .attr('class', 'label-container')
      .attr('transform', `translate(${options.sankey.nodeWidth}, ${options.sankey.nodeHeight})`)

    appendLevelLabel(labels, 'lens', 0)
    for (let i = 1; i <= maxLevel; i++) {
      appendLevelLabel(labels, 'level', i)
    }
    appendLevelLabel(labels, 'product line', maxLevel + 1)
  }

  /**
   * @param {*} container - where to append the text
   * @param {*} text - text to display on label
   * @param {*} level - level of current label
   */
  function appendLevelLabel (container, text, level) {
    /**
      `Product Line` Text should always be at the very end (x = options.headerTextMaxWidth), ie:
      LENS (level 0) + LEVEL 1 + LEVEL 2 .... + LEVEL maxLevel

      So the level for Product Line should always = maxLevel + 1

      For example, when maxLevel = 2
      - LENS - index: 0
      - LEVEL 1 - index: 1
      - LEVEL 2 - index: 2 (maxLevel)
      - Product Line - index: 3 (maxLevel + 1)
    */
    const dx = options.headerTextMaxWidth * level / (maxLevel + 1.0)

    if (text === 'level') {
      text = text + ' ' + level
    }

    container
      .append('text')
      .text(text)
      .style('fill', options.sankey.nodeTextColor)
      .style('font-weight', options.boldFontWeight)
      .style('font-size', options.labelFontSize + 'px')
      .style('text-anchor', 'middle')
      .attr('class', 'text-upper')
      .attr('transform', `translate(${dx}, ${options.sankey.nodeHeight / 2})`)
  }

  /**
   * calculate the Y values based on the positionIndex
   */
  function getYCoordinate () {
    // in case we have a straight line case, and don't want to divide by 0
    const maxPositionIndex = Math.max(positionIndex, 1)

    data.nodes.forEach(function (d) {
      d.y = d.positionIndex * 1.0 / maxPositionIndex * options.height

      // if calculated y exceeds set SVG height, keep track of the new height
      if (d.y >= maxYCoord) {
        maxYCoord = d.y
      }
    })
  }

  /** update the y position for the links based on sort order */
  function adjustLinkEndPoints (node) {
    if (node.sourceLinks.length > 0) {
      const links = node.sourceLinks.sort((a, b) => { return a.target.positionIndex - b.target.positionIndex })

      const sumChildNodes = Math.round(links.reduce((sum, link) => { return sum + link.target.value }, 0))
      const offset = ((100 - sumChildNodes) / 100) * options.sankey.nodeHeight / 2
      const stackedLinks = Math.round(node.value) >= sumChildNodes
      node.stackedLinks = stackedLinks

      let cumulativeOffset = 0
      links.forEach(function (link) {
        link.y1 = link.target.y
        link.y0 = link.source.y

        if (node.sourceLinks.length > 1 && stackedLinks) {
          const lineThickness = calculateLineThickness(link.target.value)

          link.y0 = link.y0 + offset + (lineThickness / 2) - (options.sankey.nodeHeight / 2) + cumulativeOffset
          cumulativeOffset += lineThickness
        }

        adjustLinkEndPoints(link.target)
      })
    }
  }

  /**
   * Add data attributes and content needed to render a tooltip
   */
  function addTooltips () {
    nodeContent.attr('data-controller', 'tippy-tooltip')
      .attr('data-tippy-tooltip-target', 'trigger')
      .attr('data-tippy-tooltip-max-width-value', options.tooltip.maxWidth)
      .attr('data-tippy-tooltip-theme-value', options.tooltip.theme)
      .attr('data-tippy-tooltip-placement-value', options.tooltip.position)
      .append('div')
      .attr('data-tippy-tooltip-target', 'content')
      .text((d) => { return textSplitForTooltip(d.name, options.sankey.maxNameStringLength) })
  }

  /** Add listener to change link opacity when hovering over a node */
  function addHoverEffects () {
    nodes.on('mouseenter', function (node) {
      changeLinkAndParentNodeStyles(node, options.sankey.linkOpacityActive, options.sankey.nodeHoverOutlineColor)
    }).on('mouseleave', function (node) {
      changeLinkAndParentNodeStyles(node, options.sankey.linkOpacity, options.sankey.nodeOutlineColor)
    })
  }

  /**
   * Change link opacity of given nodes
   * @param {Object} node - active target node
   * @param {Number} opacity - link opacity
   * @param {String} nodeOutlineColor
   */
  function changeLinkAndParentNodeStyles (node, opacity, nodeOutlineColor) {
    svg.selectAll(`.sankey-link[data-target-id='${node.id}`)
      .style('stroke-opacity', opacity)
    svg.select(`[data-node-id="${node.id}"] rect`).style('stroke', nodeOutlineColor)

    node.targetLinks.forEach(function (link) {
      changeLinkAndParentNodeStyles(link.source, opacity, nodeOutlineColor)
    })
  }

  /**
   * In case we have a node that's been past the max SVG height, we should make the SVG taller
   * @param {Integer} height - current height of SVG
   * @param {Integer} maxYCoord - biggest y coordinate (from the nodes)
   * @param {Integer} heightMultiplier
   * @param {Integer} offset
   */
  function updateSvgHeight (height, maxYCoord, heightMultiplier, offset) {
    if (maxYCoord >= height) {
      const newHeight = maxYCoord + heightMultiplier + offset
      svg.attr('height', newHeight)
    }
  }

  /**
   * @param {Number} value
   * @returns line thickness
   */
  function calculateLineThickness (value) {
    return Math.max((value / 100.0) * options.sankey.linkBaseThickness, options.sankey.linkMinThickness)
  }
}

global.Cog.companySankey ||= CompanySankey
